import { useCallback, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useMutation } from "@apollo/client";
import { useAtom, useAtomValue } from "jotai";

import { assistantChatMutation } from "../api";
import { type AssistantChatMutation, type AssistantChatMutationVariables } from "../api/assistantChat.gql-generated";
import { type AssistantChatMessageModel, AssistantChatRole } from "../model";
import { activeAssistantLensSnapshotAtom, assistantChatMessagesAtom } from "../state";
import { buildContextualQuestion } from "../utils";

const newId = (): string =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

interface UseAssistantChatResult {
    messages: AssistantChatMessageModel[];
    isSending: boolean;
    hasError: boolean;
    send: (question: string) => Promise<void>;
    clear: () => void;
}

export const useAssistantChat = (): UseAssistantChatResult => {
    const [messages, setMessages] = useAtom(assistantChatMessagesAtom);
    const activeLensSnapshot = useAtomValue(activeAssistantLensSnapshotAtom);
    const sendInFlightRef = useRef(false);
    const conversationVersionRef = useRef(0);
    const [isSendInFlight, setIsSendInFlight] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [mutate, { loading: isMutationSending }] = useMutation<AssistantChatMutation, AssistantChatMutationVariables>(
        assistantChatMutation,
    );
    const { pathname } = useLocation();

    const send = useCallback(
        async (rawQuestion: string): Promise<void> => {
            const question = rawQuestion.trim();
            if (question.length === 0 || sendInFlightRef.current || isMutationSending) return;

            sendInFlightRef.current = true;
            setIsSendInFlight(true);
            const conversationVersion = conversationVersionRef.current;

            try {
                const userMessage: AssistantChatMessageModel = {
                    id: newId(),
                    role: AssistantChatRole.User,
                    text: question,
                    createdAt: Date.now(),
                };
                const history = pairHistory(messages);
                const contextualQuestion = buildContextualQuestion(pathname, question, activeLensSnapshot);

                setHasError(false);
                setMessages((previousMessages) => [...previousMessages, userMessage]);

                const result = await mutate({
                    variables: { input: { question: contextualQuestion, history } },
                });
                const payload = result.data?.assistantChat;
                if (!payload) {
                    if (conversationVersionRef.current === conversationVersion) setHasError(true);
                    return;
                }
                const assistantMessage: AssistantChatMessageModel = {
                    id: newId(),
                    role: AssistantChatRole.Assistant,
                    text: payload.answer,
                    sources: payload.sources.map((source) => ({
                        id: source.id,
                        link: source.link,
                        relevanceScore: source.relevanceScore,
                    })),
                    createdAt: Date.now(),
                };
                if (conversationVersionRef.current === conversationVersion) {
                    setMessages((previousMessages) => [...previousMessages, assistantMessage]);
                }
            } catch {
                if (conversationVersionRef.current === conversationVersion) setHasError(true);
            } finally {
                sendInFlightRef.current = false;
                setIsSendInFlight(false);
            }
        },
        [activeLensSnapshot, isMutationSending, messages, mutate, pathname, setMessages],
    );

    const clear = useCallback(() => {
        conversationVersionRef.current += 1;
        setMessages([]);
        setHasError(false);
    }, [setMessages]);

    return { messages, isSending: isMutationSending || isSendInFlight, hasError, send, clear };
};

interface HistoryPair {
    prompt: string;
    response: string;
}

const pairHistory = (messages: AssistantChatMessageModel[]): HistoryPair[] => {
    const pairs: HistoryPair[] = [];
    for (let index = 0; index + 1 < messages.length; index += 1) {
        const prompt = messages[index];
        const response = messages[index + 1];
        if (prompt?.role === AssistantChatRole.User && response?.role === AssistantChatRole.Assistant) {
            pairs.push({ prompt: prompt.text, response: response.text });
            index += 1;
        }
    }
    return pairs;
};
